import { FC, HTMLAttributes, useRef, useState, useEffect, useMemo, useCallback } from "react";
import styled, { css } from "styled-components";
import { HudBoard } from "types";
import { TextStyle } from "pixi.js";
import { useHands, usePrizes, useSkip, usePlayer, useCards, usePot, usePlayers, useTournamentDetails, useForceUpdate } from "hooks";
import BigWinImg from "assets/images/apng/big-win.png";
import BountyWinImg from "assets/images/apng/bounty-winner.png";
import JackpotWinImg from "assets/images/apng/jackpot-winner.png";
import FireworksImg from "assets/images/apng/fireworks.png";
import GoldCoinsImg from "assets/images/apng/gold-coins.png";
import { ApngPlayer, ApngPlayerHandle } from "components/ApngPlayer";
import { animated, useSpring, useTransition } from "@react-spring/web";
import { useStore } from "utils/store";
import { ChipCount } from "components/ChipCount";
import { getBb } from "utils/number";
import { useSoundStore } from "utils/sound-store";
import { Sounds } from "types/sound";
import { useDebounceCallback, useTimeout, useWindowSize } from "usehooks-ts";
import { useOrientation } from "hooks/orientation";

const Container = styled.div`
  left: 0;
  width: 100%;
  height: 100%;
  position: relative;
  transform-origin: top left;
  pointer-events: none;
  grid-row-start: 1;
  grid-column-start: 1;
  z-index: 1;

  @media (orientation: landscape) {
    margin-left: -1.73vw;
  }
`;

const Result = styled.div<{ $scale: number }>`
  position: relative;
  width: 100%;
  height: 72%;
  transform: scale(${props => props.$scale});

  @media (orientation: portrait) {
    height: 76px;
    margin-bottom: 4px;
  }
`;

const Award = styled(animated.div)<{ $type: number }>`
  position: absolute;
  z-index: 1;
  width: 250px;
  height: 250px; 
  ${props => {
    if(props.$type < 1)
      return css`
        left: calc((100% - 250px) / 2);
        top: calc((100% - 260px) / 2);
      `;
    else if(props.$type < 2)
      return css`
        left: calc((100% - 245px) / 2);
        top: calc((100% - 265px) / 2);
      `;
    else if(props.$type < 3)
      return css`
        left: calc((100% - 190px) / 2);
        top: calc((100% - 208px) / 2);
        width: 190px;
        height: 190px; 
      `;
    else
      return css`
        left: calc((100% - 100px) / 2);
        top: calc((100% - 75px) / 2);
        width: 100px;
        height: 75px; 
      `;
  }}

  & > div {
    position: absolute;
  }

  @media (orientation: portrait) {
    width: 240px;
    height: 240px; 
    ${props => {
      if(props.$type < 1)
        return css`
          left: calc((100% - 240px) / 2);
          top: calc((100% - 250px) / 2);
        `;
      else if(props.$type < 2)
        return css`
          left: calc((100% - 235px) / 2);
          top: calc((100% - 255px) / 2);
        `;
      else if(props.$type < 3)
        return css`
          left: calc((100% - 230px) / 2);
          top: calc((100% - 265px) / 2);
        `;
      else
        return css`
          left: calc((100% - 140px) / 2);
          top: calc((100% - 105px) / 2);
          width: 140px;
          height: 105px; 
        `;
    }}
  }
`;

const GoldCoins = styled.div`
  width: 220px;
  height: 220px;
  left: calc((100% - 220px) / 2);
  top: calc((100% - 220px) / 2);
  position: absolute;
`;

const Ray = styled(animated.div)`
  position: absolute;
  width: 230px;
  height: 230px;
  left: calc((100% - 230px) / 2);
  top: calc((100% - 230px) / 2);
  background: url(images/ray.png) center / contain no-repeat;
`;

const BigLoss = styled(animated.div)`
  width: 100%;
  height: 100%;
  background: url(images/big-loss.png) center / contain no-repeat;
`;

export type HudProps = HTMLAttributes<HTMLDivElement> & {
  onHand: () => void;
  onMcip: () => void;
  onLastCard: () => void;
  onWinLose: () => void;
  onFinish: () => void;
  winnerShown: boolean;
};

const Fireworks = ({ width = 0, height = 0, portrait = false }) => {
  const scaleX = width / (portrait ? 640 : 1268);
  const scaleY = height / (portrait ? 1150 : 470);
  const pos = [
    { x: 0, y: 0 },
    { x: 15, y: 30 },
    { x: -40, y: 15 },
    { x: 20, y: -40 },
    { x: -25, y: -10 },
  ];

  return (
    <>
      {pos.map((value, i) =>
        <ApngPlayer 
          key={i}
          src={FireworksImg}
          delay={0.25 * i}
          rate={2.25}
          style={{
            position: "absolute",
            width: "292px",
            height: "292px",
            left: `${value.x / scaleX - 65}px`,
            top: `${value.y / scaleY - 75}px`,
            filter: `sepia(100%) saturate(3000%) hue-rotate(${Math.floor(Math.random() * 360)}deg) brightness(90%)`,
            zIndex: 1,
          }}
        />
      )}
    </>
  );
};

export const Hud: FC<HudProps> = ({ onHand, onMcip, onLastCard, onWinLose, onFinish, winnerShown, ...props }) => {
  const boardRef = useRef<HudBoard>(HudBoard.Hand);
  const potBoardRef = useRef<HudBoard>(HudBoard.Pot);
  const { currentHandId: handId, handIds, bigBlind } = useHands();
  const { amount, time, winnings, bountyWinnings, bank } = usePlayer();
  const { winType, buyIn } = useTournamentDetails();
  const { playerIds } = usePlayers();
  const optionsRef = useRef<string[]>([]);
  const [chipValue, setChipValue] = useState('');
  const timeRef = useRef(0);
  const { isLastCard } = useCards();
  const { isJackpotWin, isBountyWin, isBigWin, isBigLose } = usePrizes();
  const [count, setCount] = useState(-1);
  const showPrize = useStore(state => state.showPrize);
  const coinRef = useRef<ApngPlayerHandle>(null);
  const skip = useSkip();
  const pot = usePot();
  const potValueRef = useRef('');
  const { forceUpdate } = useForceUpdate();
  const play = useSoundStore(store => store.play);
  const fadeOut = useSoundStore(store => store.fadeOut);
  const stopAll = useSoundStore(store => store.stopAll);
  const { portrait } = useOrientation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  const { width, height } = useWindowSize();

  const [resultSpring, resultApi] = useSpring(() => ({
    opacity: 0,
    config: {
      duration: 500,
    }
  }));

  useEffect(() => {
    const w = containerRef.current?.clientWidth || 0;
    const h = containerRef.current?.clientHeight || 0;
    if(portrait)
      setScale(Math.min(w / 148, h / 183));
    else
      setScale(Math.min(w / 130, h / 98));
  }, [containerRef.current?.clientWidth, containerRef.current?.clientHeight, portrait]);

  const checkCard = useCallback(() => {
    isLastCard ? onLastCard() : onMcip();
  }, [isLastCard, onLastCard, onMcip]);

  useEffect(() => {
    if(skip){
      stopAll();
      onLastCard();
    }
  }, [onLastCard, skip, stopAll]);

  useTimeout(onFinish, skip ? 250 : null);

  useEffect(() => {
    if (handId <= 0 || !handIds) return;
    boardRef.current = HudBoard.Hand;
    potValueRef.current = '';
    optionsRef.current = handIds.split(',');
    setChipValue(handId.toString());
  }, [handId, handIds]);

  useEffect(() => {
    if ((!amount && !winnings && !winType) || !bigBlind || skip || time < 0) return;
    
    const t = setTimeout(() => {
      const winBet = winnings + amount;
      if(!!winnings || !!winType.length){
        if(!portrait) potBoardRef.current = winBet === pot ? HudBoard.Win : HudBoard.Blank;
        boardRef.current = potBoardRef.current === HudBoard.Win ? HudBoard.Blank : !winBet ? HudBoard.Lose : HudBoard.Win;
      }else{
        potBoardRef.current = HudBoard.Pot;
        boardRef.current = HudBoard.Mcip;
      }
      const prevPot = potValueRef.current;
      potValueRef.current = portrait && boardRef.current !== HudBoard.Mcip ? '' : getBb(pot, bigBlind) + ' BB';
      const prevTime = timeRef.current;
      timeRef.current = time;
      const bb = getBb(Math.abs(!winBet ? winnings : winBet), bigBlind) + ' BB';
      if(chipValue === bb){
        if(prevPot !== potValueRef.current || !!winType){
          forceUpdate();
        }else if(timeRef.current !== prevTime){
          checkCard();
        }
      }else{
        optionsRef.current = [chipValue, bb];
        setChipValue(bb);
      }
    }, boardRef.current === HudBoard.Hand ? playerIds.length * 250 + 1250 : 750); 

    return () => {
      clearTimeout(t);
    };
  }, [amount, bigBlind, checkCard, chipValue, forceUpdate, playerIds.length, portrait, pot, skip, time, winType, winnings]);

  const prizes = useMemo(() => {
    return [isJackpotWin, isBountyWin, isBigWin, isBigLose];
  }, [isBigLose, isBigWin, isBountyWin, isJackpotWin]);

  const isLose = useMemo(() => {
    return bountyWinnings < 0 && !isBigLose;
  }, [bountyWinnings, isBigLose]);

  const debWinLose = useDebounceCallback(useCallback(onWinLose, [onWinLose]), 1000);

  const debFinish = useDebounceCallback(
    useCallback(() => {
      fadeOut(Sounds.CoinDrop);
      fadeOut(Sounds.Fireworks);
      onFinish();
    }, [fadeOut, onFinish]), 
    isLose ? 2000 : 1000
  );

  useEffect(() => {
    if(showPrize && !skip){
      if(!prizes.includes(true) || count === prizes.length){
        if(isLose) debWinLose();
        debFinish();
      }else if(count < 0){
        setCount(prizes.indexOf(true));
      }
    }else{
      setCount(-1);
    }
  }, [count, debFinish, debWinLose, isLose, prizes, showPrize, skip]);

  const handleApng = useCallback(() => {
    switch(count){
      case 0:
        fadeOut(Sounds.JackpotWin);
        fadeOut(Sounds.Fireworks);
        break;
      case 1:
        fadeOut(Sounds.BountyWin);
        break;
      case 2: 
        fadeOut(Sounds.BigWin);
        break;
    }
    coinRef.current?.stop();
    const n = prizes.indexOf(true, count + 1);
    setCount(n < 0 ? prizes.length : n);
  }, [count, fadeOut, prizes]);

  useEffect(() => {
    if(winnerShown && isJackpotWin) handleApng();
  }, [handleApng, isJackpotWin, winnerShown]);

  useEffect(() => {
    if(!isJackpotWin || count !== 0) return;

    play(Sounds.JackpotWin);
    play(Sounds.Fireworks);
    play(Sounds.CoinDrop);
  }, [isJackpotWin, count, play]);

  useTimeout(onWinLose, !prizes.includes(true) || count < 0 || count > 3 ? null : 1500);

  useEffect(() => {
    switch(count){
      case 1:
        if(prizes[1]){
          play(Sounds.BountyWin);
          play(Sounds.CoinDrop);
        }
        break;
      case 2:
        if(prizes[2]){
          play(Sounds.BigWin);
          play(Sounds.Fireworks);
        }
        break;
      case 3:
        if(prizes[3]){
          play(Sounds.BigLoss);
          resultApi.start({
            opacity: 1,
            delay: 500,
            onRest: () => {
              resultApi.start({
                opacity: 0,
                delay: 2000,
                onRest: handleApng
              })
            }
          });
        }
        break;
    }
  }, [count, handleApng, play, prizes, resultApi]);

  const textStyle = (isPot?: boolean) => {
    return new TextStyle({
      align: "center",
      fontFamily: "Teko",
      fontSize: portrait ? 72 : 36,
      fontWeight: "500",
      fill: isPot ? ['#eea857', '#efe090', '#eea857'] : boardRef.current === HudBoard.Lose ? "#ff1800" : "#fff",
    });
  };

  const { transform } = useSpring({
    from: { transform: "rotate(0deg)" },
    to: { transform: "rotate(360deg)" },
    loop: true,
    config: {
      duration: 10000,
    },
  });

  const transitions = useTransition(count, {
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: {
      duration: 500,
    }
  });

  const hasPrize = (n: number) => {
    return count === n && prizes[count];
  }

  const handlePot = () => {
    switch(boardRef.current){
      case HudBoard.Mcip:
        checkCard();
        break;
    }
  };

  const handleCount = () => {
    if(boardRef.current === HudBoard.Hand){
      onHand();
    }else if(portrait && [HudBoard.Win, HudBoard.Lose].includes(boardRef.current)){
      checkCard();
    }
  };

  const showFireworks = (hasPrize(0) || hasPrize(2)) && typeof buyIn?.buyIn === 'number' && bank >= buyIn.buyIn * 5;

  return (
    <Container ref={containerRef}>
      <Result $scale={scale}>
        {transitions((style, i) => i >= 0 && i < 3 && <Ray style={{ ...style, transform }} />)}
        {transitions((style, i) => 
          (i >= 0 && i < prizes.length) && 
            <>
              {showFireworks && <Fireworks width={width} height={height} portrait={portrait} />}
              {(hasPrize(0) || hasPrize(1)) && <GoldCoins><ApngPlayer ref={coinRef} src={GoldCoinsImg} rate={2} numPlays={0} /></GoldCoins>}
              {(!winnerShown || i > 0) && 
                <Award $type={i} style={style}>
                  {hasPrize(0) && <ApngPlayer src={JackpotWinImg} rate={1.5} />}
                  {hasPrize(1) && <ApngPlayer src={BountyWinImg} rate={2.2} onEnd={handleApng} />}
                  {hasPrize(2) && <ApngPlayer src={BigWinImg} rate={4.5} onEnd={handleApng} />}
                  <BigLoss style={resultSpring} />
                </Award>
              }
            </>
        )}
      </Result>
      <ChipCount
        board={boardRef.current}
        value={chipValue}
        options={optionsRef.current}
        textStyle={textStyle()}
        skip={skip}
        scale={scale}
        onFinish={handleCount}
        style={portrait
          ? { bottom: 0 }
          : { }
        }
      />
      <ChipCount
        board={potBoardRef.current}
        value={potValueRef.current}
        isPotValue={true}
        startFrom={true}
        textStyle={textStyle(true)}
        skip={skip}
        scale={scale}
        onFinish={handlePot}
        delay={0.25}
        style={portrait
          ? { bottom: `${103 * scale + 11 * height / 1150}px` }
          : { 
              top: 0,
              left: `${!width ? 0 : 0.02 * width + ((containerRef.current?.parentElement?.clientWidth || 0) - 117) / 2}px` 
            }
        }
      />
    </Container>
  );
};
